import { useMemo } from 'react';
import type { CommandContribution, PlatformBridge, ToolHost, ViewMode } from '@slate/core';
import {
  FilePlus,
  FolderOpen,
  Save,
  Clipboard,
  X,
  Replace,
  ArrowLeft,
  ArrowRight,
  SquareSplitHorizontal,
  Eye,
  PanelLeftClose,
  ListTree,
  FolderInput,
  FolderMinus,
  Palette,
  Type,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Maximize,
  Hash,
  CircleHelp,
  Settings as SettingsIcon,
  Terminal as TerminalIcon,
  WrapText,
} from 'lucide-react';
import { useSidebarStore } from '../sidebar/sidebarStore';
import {
  useDocStore,
  READING_FONT_SCALE_MIN,
  READING_FONT_SCALE_MAX,
  type OpenDoc,
  type ReadingWidth,
  type ThemePref,
  type ThemeName,
} from '../store/docStore';
import {
  openPath,
  openFileToDoc,
  saveActiveDoc,
  saveActiveDocAs,
} from '../store/docActions';
import { useToolContributionsFor } from '../tools/useToolContributionsFor';
import { recordTerminalCommand } from '../tools/terminal/commandHistory';
import { toast } from '../store/toastStore';
import { resolveDocLanguage, LANGUAGE_CHOICES } from '../util/docLanguage';
import type { Command, CommandCtx } from './types';

/**
 * Build the imperative context a command's `run` receives. Reads live store
 * actions from the snapshot so a command always operates on current state.
 * Pure (no side effects) — side effects happen inside each command's `run`.
 */
export function buildCtx(
  platform: PlatformBridge,
  snap: ReturnType<typeof useDocStore.getState>,
): CommandCtx {
  return {
    platform,
    newUntitled: snap.newUntitled,
    activateDoc: snap.activateDoc,
    requestCloseDocs: snap.requestCloseDocs,
    toggleSplit: snap.toggleSplit,
    setViewMode: snap.setViewMode,
    toggleSidebar: () => useSidebarStore.getState().toggleOpen(),
    toggleOutlinePinned: snap.toggleOutlinePinned,
    setThemePref: snap.setThemePref,
    setTheme: snap.setTheme,
    addWorkspaceRoot: snap.addWorkspaceRoot,
    removeWorkspaceRoot: snap.removeWorkspaceRoot,
    setReadingFontScale: snap.setReadingFontScale,
    setReadingWidth: snap.setReadingWidth,
    setPendingJumpLine: snap.setPendingJumpLine,
    setDocLanguage: snap.setDocLanguage,
    toggleActiveDocWrap: () => {
      if (snap.activeId) snap.toggleDocWrap(snap.activeId);
    },
  };
}

/** Dispatch a window-level CustomEvent (App listens for these dialog opens). */
function emit(eventName: string): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(eventName));
  }
}

// ─────────────────────────── File ───────────────────────────
function fileCommands(
  ctx: CommandCtx,
  deps: { hasActive: boolean; activeDoc?: OpenDoc; folders: boolean },
): Command[] {
  const { hasActive, activeDoc, folders } = deps;
  return [
    {
      id: 'doc.new',
      name: 'New document',
      synonyms: ['create', 'untitled'],
      slash: '/new',
      group: 'File',
      icon: FilePlus,
      enabled: true,
      run: () => {
        ctx.newUntitled();
      },
    },
    {
      id: 'doc.open',
      name: 'Open file or folder',
      synonyms: ['file', 'folder', 'directory'],
      slash: '/open',
      group: 'File',
      icon: FolderOpen,
      enabled: true,
      params: [{ name: 'path', kind: 'path', pathMode: 'file', rootSource: 'workspace' }],
      run: async (c, args) => {
        const p = (args.path as string | undefined)?.trim();
        if (!p) {
          await openFileToDoc(c.platform);
          return;
        }
        // Route directories to add-workspace-root; files to openPath (which reads + opens).
        const isDir = (await c.platform.isDirectory?.(p)) === true;
        if (isDir) {
          const name = p.split(/[\\/]/).filter(Boolean).pop() ?? p;
          const ref = { id: p, path: p, name };
          c.addWorkspaceRoot(ref);
          void c.platform.pushRecentWorkspace(ref).catch(() => {});
        } else {
          await openPath(c.platform, p);
        }
      },
    },
    {
      id: 'doc.open-folder',
      name: 'Open folder…',
      synonyms: ['workspace', 'directory'],
      slash: '/open-folder',
      group: 'File',
      icon: FolderInput,
      enabled: folders,
      run: async (c) => {
        const picked = await c.platform.openFolderDialog();
        if (picked) {
          c.addWorkspaceRoot(picked);
          void c.platform.pushRecentWorkspace(picked).catch(() => {});
        }
      },
    },
    {
      id: 'doc.save',
      name: 'Save',
      slash: '/save',
      group: 'File',
      icon: Save,
      shortcut: 'Cmd+S',
      enabled: !!activeDoc && activeDoc.dirty && !!activeDoc.ref,
      run: async (c) => {
        await saveActiveDoc(c.platform);
      },
    },
    {
      id: 'doc.save-as',
      name: 'Save as…',
      synonyms: ['export'],
      slash: '/save-as',
      group: 'File',
      icon: Save,
      enabled: hasActive,
      run: async (c) => {
        await saveActiveDocAs(c.platform);
      },
    },
    {
      id: 'doc.clipboard',
      name: 'New from clipboard',
      synonyms: ['paste'],
      slash: '/clipboard',
      group: 'File',
      icon: Clipboard,
      enabled: true,
      run: async (c) => {
        let text = '';
        try {
          text = (await navigator.clipboard?.readText?.()) ?? '';
        } catch {
          /* clipboard unavailable / denied — open an empty buffer */
        }
        c.newUntitled(text);
      },
    },
  ];
}

// ─────────────────────────── Tabs ───────────────────────────
function tabCommands(
  ctx: CommandCtx,
  deps: { tabCount: number; activeId: string | null },
): Command[] {
  const { tabCount, activeId } = deps;
  return [
    {
      id: 'tab.close',
      name: 'Close tab',
      slash: '/close',
      group: 'Tabs',
      icon: X,
      enabled: tabCount >= 1 && !!activeId,
      run: () => {
        if (activeId) ctx.requestCloseDocs([activeId]);
      },
    },
    {
      id: 'tab.close-others',
      name: 'Close other tabs',
      slash: '/close-others',
      group: 'Tabs',
      icon: X,
      enabled: tabCount >= 2,
      run: () => {
        const s = useDocStore.getState();
        s.requestCloseDocs(s.docs.filter((d) => d.id !== s.activeId).map((d) => d.id));
      },
    },
    {
      id: 'tab.close-all',
      name: 'Close all tabs',
      slash: '/close-all',
      group: 'Tabs',
      icon: X,
      enabled: tabCount >= 1,
      run: () => {
        const s = useDocStore.getState();
        s.requestCloseDocs(s.docs.map((d) => d.id));
      },
    },
    {
      id: 'tab.next',
      name: 'Next tab',
      slash: '/next-tab',
      group: 'Tabs',
      icon: ArrowRight,
      enabled: tabCount >= 2,
      run: () => {
        const s = useDocStore.getState();
        if (s.docs.length < 2) return;
        const idx = s.docs.findIndex((d) => d.id === s.activeId);
        const next = s.docs[(idx + 1 + s.docs.length) % s.docs.length];
        if (next) s.activateDoc(next.id);
      },
    },
    {
      id: 'tab.prev',
      name: 'Previous tab',
      slash: '/prev-tab',
      group: 'Tabs',
      icon: ArrowLeft,
      enabled: tabCount >= 2,
      run: () => {
        const s = useDocStore.getState();
        if (s.docs.length < 2) return;
        const idx = s.docs.findIndex((d) => d.id === s.activeId);
        const prev = s.docs[(idx - 1 + s.docs.length) % s.docs.length];
        if (prev) s.activateDoc(prev.id);
      },
    },
    {
      id: 'tab.switch-to',
      name: 'Switch to tab…',
      synonyms: ['goto', 'activate'],
      slash: '/switch-to',
      group: 'Tabs',
      icon: Replace,
      enabled: tabCount >= 1,
      params: [{ name: 'tab', kind: 'tab' }],
      run: (c, args) => {
        const id = args.tab as string | undefined;
        if (id) c.activateDoc(id);
      },
    },
  ];
}

// ─────────────────────────── View ───────────────────────────
function viewCommands(
  ctx: CommandCtx,
  deps: { isMd: boolean; canSetLanguage: boolean },
): Command[] {
  const { isMd, canSetLanguage } = deps;
  return [
    {
      id: 'view.split',
      name: 'Toggle split view',
      synonyms: ['side by side', 'preview'],
      slash: '/split',
      group: 'View',
      icon: SquareSplitHorizontal,
      shortcut: 'Cmd+\\',
      enabled: true,
      run: () => {
        ctx.toggleSplit();
      },
    },
    {
      id: 'view.toggle-wrap',
      name: 'Toggle soft wrap',
      synonyms: ['word wrap', 'line wrap', 'wrap lines'],
      slash: '/wrap',
      group: 'View',
      icon: WrapText,
      shortcut: 'Alt+Z',
      enabled: canSetLanguage,
      run: (c) => {
        c.toggleActiveDocWrap();
      },
    },
    {
      id: 'view.set',
      name: 'Set view',
      synonyms: ['mode', 'editor', 'preview'],
      slash: '/view',
      group: 'View',
      icon: Eye,
      shortcut: 'Cmd+1/2/3',
      enabled: isMd,
      params: [
        {
          name: 'mode',
          kind: 'enum',
          values: [
            { value: 'plaintext', label: 'Plain text' },
            { value: 'wysiwyg', label: 'WYSIWYG' },
            { value: 'rendered', label: 'Rendered preview' },
          ],
        },
      ],
      run: (c, args) => {
        c.setViewMode(args.mode as ViewMode);
      },
    },
    {
      id: 'view.language',
      name: 'Change language',
      synonyms: ['lang', 'syntax', 'highlight', 'mode', 'plaintext', 'markdown'],
      slash: '/lang',
      group: 'View',
      icon: Type,
      enabled: canSetLanguage,
      params: [
        {
          name: 'language',
          kind: 'enum',
          values: [
            { value: 'auto', label: 'Auto (from extension)' },
            ...LANGUAGE_CHOICES.map((c) => ({ value: c.key, label: c.label })),
          ],
        },
      ],
      run: (c, args) => {
        const activeId = useDocStore.getState().activeId;
        if (!activeId) return;
        const value = String(args.language ?? '');
        c.setDocLanguage(activeId, value === 'auto' ? null : value);
      },
    },
    {
      id: 'view.sidebar',
      name: 'Toggle sidebar',
      synonyms: ['files', 'explorer'],
      slash: '/sidebar',
      group: 'View',
      icon: PanelLeftClose,
      enabled: true,
      run: () => {
        ctx.toggleSidebar();
      },
    },
    {
      id: 'view.outline',
      name: 'Toggle outline',
      synonyms: ['headings', 'toc', 'table of contents'],
      slash: '/outline',
      group: 'View',
      icon: ListTree,
      enabled: isMd,
      run: () => {
        ctx.toggleOutlinePinned();
      },
    },
  ];
}

// ───────────────────────── Workspace ────────────────────────
function workspaceCommands(
  deps: { folders: boolean; workspaceRootCount: number },
): Command[] {
  const { folders, workspaceRootCount } = deps;
  return [
    {
      id: 'ws.add-root',
      name: 'Add workspace folder…',
      synonyms: ['root', 'open folder'],
      slash: '/add-root',
      group: 'Workspace',
      icon: FolderInput,
      enabled: folders,
      run: async (c) => {
        const picked = await c.platform.openFolderDialog();
        if (picked) {
          c.addWorkspaceRoot(picked);
          void c.platform.pushRecentWorkspace(picked).catch(() => {});
        }
      },
    },
    {
      id: 'ws.remove-root',
      name: 'Remove workspace folder…',
      synonyms: ['close folder', 'root'],
      slash: '/remove-root',
      group: 'Workspace',
      icon: FolderMinus,
      enabled: workspaceRootCount >= 1,
      params: [{ name: 'root', kind: 'root' }],
      run: (c, args) => {
        const id = args.root as string | undefined;
        if (id) c.removeWorkspaceRoot(id);
      },
    },
    {
      id: 'ws.close-all',
      name: 'Close all workspace folders…',
      synonyms: ['close roots', 'remove roots'],
      slash: '/close-all-workspaces',
      group: 'Workspace',
      icon: FolderMinus,
      shortcut: 'Cmd+Shift+W',
      enabled: workspaceRootCount >= 1,
      run: () => {
        emit('slate:close-all-workspaces');
      },
    },
  ];
}

// ─────────────────────────── Theme ──────────────────────────
function themeCommands(): Command[] {
  return [
    {
      id: 'theme.set',
      name: 'Set color theme',
      synonyms: ['dark', 'light', 'appearance'],
      slash: '/theme',
      group: 'Theme',
      icon: Palette,
      enabled: true,
      params: [
        {
          name: 'mode',
          kind: 'enum',
          values: [{ value: 'light' }, { value: 'dark' }, { value: 'system' }],
        },
      ],
      run: (c, args) => {
        c.setThemePref(args.mode as ThemePref);
      },
    },
    {
      id: 'theme.typography',
      name: 'Set typography',
      synonyms: ['font', 'serif', 'sans', 'reader'],
      slash: '/typography',
      group: 'Theme',
      icon: Type,
      enabled: true,
      params: [
        {
          name: 'value',
          kind: 'enum',
          values: [
            { value: 'sans-reader', label: 'Sans · Reader' },
            { value: 'sans-contrast', label: 'Sans · Contrast' },
            { value: 'serif-reader', label: 'Serif · Reader' },
            { value: 'serif-contrast', label: 'Serif · Contrast' },
          ],
        },
      ],
      run: (c, args) => {
        c.setTheme(args.value as ThemeName);
      },
    },
  ];
}

// ────────────────────────── Reading ─────────────────────────
function readingCommands(
  deps: { isMd: boolean; readingFontScale: number },
): Command[] {
  const { isMd, readingFontScale } = deps;
  return [
    {
      id: 'reading.zoom',
      name: 'Set reading zoom',
      synonyms: ['font size', 'scale'],
      slash: '/zoom',
      group: 'Reading',
      icon: ZoomIn,
      enabled: isMd,
      params: [
        {
          name: 'value',
          kind: 'number',
          range: [READING_FONT_SCALE_MIN, READING_FONT_SCALE_MAX],
          presets: [0.7, 0.85, 1.0, 1.25, 1.5, 1.6],
          displayUnit: '%',
        },
      ],
      run: (c, args) => {
        c.setReadingFontScale(args.value as number);
      },
    },
    {
      id: 'reading.zoom-in',
      name: 'Zoom in',
      slash: '/zoom-in',
      group: 'Reading',
      icon: ZoomIn,
      enabled: isMd && readingFontScale < READING_FONT_SCALE_MAX,
      run: (c) => {
        const cur = useDocStore.getState().readingFontScale;
        c.setReadingFontScale(Math.min(READING_FONT_SCALE_MAX, cur + 0.1));
      },
    },
    {
      id: 'reading.zoom-out',
      name: 'Zoom out',
      slash: '/zoom-out',
      group: 'Reading',
      icon: ZoomOut,
      enabled: isMd && readingFontScale > READING_FONT_SCALE_MIN,
      run: (c) => {
        const cur = useDocStore.getState().readingFontScale;
        c.setReadingFontScale(Math.max(READING_FONT_SCALE_MIN, cur - 0.1));
      },
    },
    {
      id: 'reading.zoom-reset',
      name: 'Reset reading zoom',
      synonyms: ['100%'],
      slash: '/zoom-reset',
      group: 'Reading',
      icon: RotateCcw,
      enabled: isMd,
      run: (c) => {
        c.setReadingFontScale(1.0);
      },
    },
    {
      id: 'reading.width',
      name: 'Set reading width',
      synonyms: ['column', 'narrow', 'wide'],
      slash: '/width',
      group: 'Reading',
      icon: Maximize,
      enabled: isMd,
      params: [
        {
          name: 'value',
          kind: 'enum',
          values: [{ value: 'narrow' }, { value: 'wide' }, { value: 'full' }],
        },
      ],
      run: (c, args) => {
        c.setReadingWidth(args.value as ReadingWidth);
      },
    },
  ];
}

// ────────────────────────── Outline ─────────────────────────
function outlineCommands(deps: { isMd: boolean }): Command[] {
  const { isMd } = deps;
  return [
    {
      id: 'outline.jump',
      name: 'Jump to heading…',
      synonyms: ['goto heading', 'navigate'],
      slash: '/jump',
      group: 'Outline',
      icon: Hash,
      enabled: isMd,
      params: [{ name: 'heading', kind: 'heading' }],
      run: (c, args) => {
        const line = Number(args.heading);
        if (Number.isFinite(line)) c.setPendingJumpLine(line);
      },
    },
  ];
}

// ─────────────────────────── Help ───────────────────────────
function helpCommands(): Command[] {
  return [
    {
      id: 'help.about',
      name: 'About Slate',
      slash: '/about',
      group: 'Help',
      icon: CircleHelp,
      enabled: true,
      run: () => {
        emit('slate:open-about');
      },
    },
    {
      id: 'help.settings',
      name: 'Settings',
      synonyms: ['preferences', 'config'],
      slash: '/settings',
      group: 'Help',
      icon: SettingsIcon,
      shortcut: 'Cmd+,',
      enabled: true,
      run: () => {
        emit('slate:open-settings');
      },
    },
    // `/keybindings` deferred — needs a keybindings overview dialog. Re-add when that ships.
  ];
}

// ───────────────────────── Terminal ─────────────────────────
/** Slash command to open a terminal and optionally run a command in it. */
export function terminalCommands(
  toolHost: ToolHost | undefined,
  deps: { ptyAvailable: boolean },
): Command[] {
  if (!toolHost || !deps.ptyAvailable) return [];
  const host = toolHost;
  return [
    {
      id: 'terminal.run',
      name: 'New terminal / run command',
      synonyms: ['shell', 'cmd', 'console', 'bash', 'zsh', 'run'],
      slash: '/cmd',
      aliases: ['/terminal'],
      group: 'Tools',
      icon: TerminalIcon,
      enabled: true,
      params: [{ name: 'command', kind: 'shell' }],
      run: (_c, args) => {
        const command = (args.command as string | undefined)?.trim();
        host.openTool('terminal', command ? { initialCommand: command } : undefined);
        if (command) recordTerminalCommand(command);
      },
    },
  ];
}

// ─────────────────────────── CLI ────────────────────────────
/**
 * Install / remove the `slate` terminal command. Gated exactly like
 * {@link terminalCommands}: surfaces nothing unless the platform reports the
 * `cli` capability AND provides the {@link CliApi} bridge (desktop only; the
 * web build leaves both falsy). Outcomes are reported through the shared toast
 * deck — the install rejection carries the manual-install instructions, so it
 * must reach the user verbatim.
 */
export function cliCommands(platform: PlatformBridge): Command[] {
  if (platform.capabilities.cli !== true || !platform.cli) return [];
  const cli = platform.cli;
  return [
    {
      id: 'cli.install',
      name: "Install 'slate' command in PATH",
      synonyms: ['cli', 'terminal command', 'shell command', 'path'],
      slash: '/install-cli',
      aliases: ['/install', '/cli'],
      group: 'Application',
      icon: TerminalIcon,
      enabled: true,
      run: async () => {
        try {
          const path = await cli.install();
          toast(`Installed 'slate' at ${path}`, { kind: 'info' });
        } catch (err) {
          // The rejection message contains the manual-install fallback
          // (e.g. when /usr/local/bin is not writable). Show it sticky so it
          // can't auto-dismiss before the user reads the instructions.
          toast(String(err instanceof Error ? err.message : err), {
            kind: 'error',
            sticky: true,
          });
        }
      },
    },
    {
      id: 'cli.uninstall',
      name: "Remove 'slate' command from PATH",
      synonyms: ['cli', 'uninstall slate'],
      slash: '/uninstall-cli',
      aliases: ['/uninstall'],
      group: 'Application',
      icon: TerminalIcon,
      enabled: true,
      run: async () => {
        try {
          await cli.uninstall();
          toast("Removed 'slate' from PATH", { kind: 'info' });
        } catch (err) {
          // Symmetric with install: the rejection message carries the
          // remediation ("remove it manually at <path>"). Show it sticky so it
          // can't auto-dismiss before the user reads the instructions.
          toast(String(err instanceof Error ? err.message : err), {
            kind: 'error',
            sticky: true,
          });
        }
      },
    },
  ];
}

// ─────────────────────────── Tools ──────────────────────────
/**
 * Convert a keymap-notation keybinding (`'CmdOrCtrl+Shift+\`'`, `'F6'`) into a
 * human-readable label matching the palette's existing `shortcut` style
 * (`Cmd+Shift+\``). The actual binding fires through the keymap registry
 * (Task 2.6); this is display-only.
 */
function formatKeybindingLabel(spec: string): string {
  return spec
    .split('+')
    .map((part) => {
      const p = part.trim();
      const lp = p.toLowerCase();
      if (lp === 'cmdorctrl' || lp === 'mod') return 'Cmd';
      if (lp === 'option') return 'Alt';
      return p;
    })
    .join('+');
}

/**
 * Map each registered tool's `commands` contribution into a palette `Command`.
 * The adapted command's `run` ignores the palette `CommandCtx`/args and invokes
 * the contribution against the live {@link ToolHost}. When `toolHost` is absent
 * (e.g. a platform without tools), no tool commands surface.
 */
/**
 * Tool `commands` contributions superseded by a richer built-in palette
 * command, so they're hidden from the palette. Their keybindings still fire
 * (bound independently through the keymap registry). `terminal.new` is replaced
 * by the `/cmd` / `/terminal` command.
 */
const SUPERSEDED_TOOL_COMMANDS = new Set<string>(['terminal.new']);

function toolCommands(
  contributions: readonly CommandContribution[],
  toolHost: ToolHost | undefined,
): Command[] {
  if (!toolHost || contributions.length === 0) return [];
  const host = toolHost;
  return contributions
    .filter((c) => !SUPERSEDED_TOOL_COMMANDS.has(c.id))
    .map((c) => ({
    id: c.id,
    name: c.title,
    // Slash form derived from the contribution id (e.g. `terminal.new` →
    // `/terminal-new`). Lets users find tool commands via the slash grammar
    // alongside built-ins like `/open`, `/save`, etc.
    slash: `/${c.id.replace(/\./g, '-')}`,
    group: 'Tools' as const,
    icon: TerminalIcon,
    shortcut: c.keybinding ? formatKeybindingLabel(c.keybinding) : undefined,
    enabled: true,
    run: () => {
      void c.run(host);
    },
  }));
}

/**
 * React hook returning a freshly-derived command catalogue on every render.
 * Each command's `enabled` is computed from current store state. Subscribes to
 * the store slices that any `enabled` reads so consumers re-render (and the
 * memoised catalogue recomputes) when those change.
 *
 * Handlers that need fresh state at run time read `useDocStore.getState()`
 * directly (e.g. tab cycling, zoom nudges); static handlers use `ctx`'s actions.
 */
export function useCommands(platform: PlatformBridge, toolHost?: ToolHost): Command[] {
  // Subscribe to every field a command's `enabled` reads.
  const docs = useDocStore((s) => s.docs);
  const activeId = useDocStore((s) => s.activeId);
  const workspaceRoots = useDocStore((s) => s.workspaceRoots);
  const readingFontScale = useDocStore((s) => s.readingFontScale);
  // Tool `commands` contributions — re-derives the catalogue when a tool is
  // registered/unregistered (the snapshot reference is stable in between).
  const toolCommandContributions = useToolContributionsFor('commands');

  return useMemo(() => {
    const snap = useDocStore.getState();
    const ctx = buildCtx(platform, snap);
    const tabCount = docs.length;
    const folders = platform.capabilities.folders === true;

    // "Active doc is text" — `kind === undefined` (back-compat) and `'text'`
    // both count as text; only `'image'` is excluded. Commands that mutate or
    // read markdown content gate on this.
    const activeDoc = docs.find((d) => d.id === activeId);
    const hasActive = !!activeDoc;
    // Markdown-only commands (`/view`, `/outline`, reading-mode, `/jump`) follow
    // the EFFECTIVE language: an image is never markdown, an untitled buffer is
    // plain text until `/lang markdown`, and a `/lang` override drives the rest.
    const isMd = hasActive && resolveDocLanguage(activeDoc).isMarkdown;
    // `/lang` works on any editable text doc (image docs have no language).
    const canSetLanguage = hasActive && activeDoc.kind !== 'image';
    const ptyAvailable = platform.capabilities.pty === true && !!toolHost;

    return [
      ...fileCommands(ctx, { hasActive, activeDoc, folders }),
      ...tabCommands(ctx, { tabCount, activeId }),
      ...viewCommands(ctx, { isMd, canSetLanguage }),
      ...workspaceCommands({ folders, workspaceRootCount: workspaceRoots.length }),
      ...themeCommands(),
      ...readingCommands({ isMd, readingFontScale }),
      ...outlineCommands({ isMd }),
      ...toolCommands(toolCommandContributions, toolHost),
      ...terminalCommands(toolHost, { ptyAvailable }),
      ...cliCommands(platform),
      ...helpCommands(),
    ];
  }, [docs, activeId, workspaceRoots, readingFontScale, platform, toolCommandContributions, toolHost]);
}
